import api from './api';

export interface ObjectiveMilestone {
  id: string;
  objectiveId: string;
  title: string;
  description?: string;
  targetDate?: string;
  completionDate?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
}

export interface Objective {
  id: string;
  userId: string;
  employeeId?: string;
  title: string;
  description?: string;
  category?: string;
  targetDate?: string;
  completionDate?: string;
  status: 'active' | 'completed' | 'cancelled' | 'on_hold';
  priority: 'low' | 'medium' | 'high';
  progress: number;
  notes?: string;
  milestones?: ObjectiveMilestone[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateObjectiveData {
  title: string;
  description?: string;
  category?: string;
  targetDate?: string;
  completionDate?: string;
  status?: 'active' | 'completed' | 'cancelled' | 'on_hold';
  priority?: 'low' | 'medium' | 'high';
  progress?: number;
  notes?: string;
  employeeId?: string;
}

export interface UpdateObjectiveData {
  title?: string;
  description?: string;
  category?: string;
  targetDate?: string;
  completionDate?: string;
  status?: 'active' | 'completed' | 'cancelled' | 'on_hold';
  priority?: 'low' | 'medium' | 'high';
  progress?: number;
  notes?: string;
  employeeId?: string;
}

export interface CreateMilestoneData {
  title: string;
  description?: string;
  targetDate?: string;
  completionDate?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  orderIndex?: number;
}

export interface UpdateMilestoneData {
  title?: string;
  description?: string;
  targetDate?: string;
  completionDate?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  orderIndex?: number;
}

export interface ObjectiveFilters {
  status?: 'active' | 'completed' | 'cancelled' | 'on_hold';
  priority?: 'low' | 'medium' | 'high';
  category?: string;
}

export const objectivesApi = {
  getAll: async (filters?: ObjectiveFilters): Promise<Objective[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.category) params.append('category', filters.category);

    const queryString = params.toString();
    const url = `/objectives${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data.data;
  },

  getById: async (id: string): Promise<Objective> => {
    const response = await api.get(`/objectives/${id}`);
    return response.data.data;
  },

  create: async (data: CreateObjectiveData): Promise<Objective> => {
    const response = await api.post('/objectives', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdateObjectiveData): Promise<Objective> => {
    const response = await api.put(`/objectives/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/objectives/${id}`);
  },

  // Milestone methods
  getMilestones: async (objectiveId: string): Promise<ObjectiveMilestone[]> => {
    const response = await api.get(`/objectives/${objectiveId}/milestones`);
    return response.data.data;
  },

  createMilestone: async (objectiveId: string, data: CreateMilestoneData): Promise<ObjectiveMilestone> => {
    const response = await api.post(`/objectives/${objectiveId}/milestones`, data);
    return response.data.data;
  },

  updateMilestone: async (objectiveId: string, milestoneId: string, data: UpdateMilestoneData): Promise<ObjectiveMilestone> => {
    const response = await api.put(`/objectives/${objectiveId}/milestones/${milestoneId}`, data);
    return response.data.data;
  },

  deleteMilestone: async (objectiveId: string, milestoneId: string): Promise<void> => {
    await api.delete(`/objectives/${objectiveId}/milestones/${milestoneId}`);
  },
};
